import { useEffect, useState } from "react";
import { Layout } from "providers/Layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CheckboxCard,
  Hide,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Show
} from "shared/components";
import { usePlacesWidget } from "react-google-autocomplete";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Plus,
  XClose
} from "untitledui-js-base";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";

import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { useAuthContext } from "providers/Auth";
import { UnitForms } from "shared/app/UnitsTableView/components/UnitForms";
import { blankUnit } from "shared/app/UnitsTableView/constants";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { Select } from "shared/components";
import { states } from "utils/stateCodes";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Loader } from "shared/components/Loader";
import { Redirect } from "shared/components/Redirect";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types/partner";
import { isSaveUnitDisabled } from "shared/app/UnitsTableView/controller";
import { Card } from "shared/components/Card";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Switch } from "shared/components/forms/Switch";
import { Property, Unit } from "types/landlord/address";
import { useForm } from "shared/hooks/use-form";
import { createPropertyFormSchema } from "./validations";

interface Props {
  newPropertyName?: string;
  isDrawerContent?: boolean;
  onClose?: () => void;
  onSuccess?: (newProperty?: Property) => void;
}

export const AddProperty = ({
  isDrawerContent,
  newPropertyName,
  onClose,
  onSuccess
}: Props) => {
  const dictionary = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const { type, permissions, partner, partnerFeatures } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const propertyForm = useForm(
    {
      name: newPropertyName || "",
      owner: "",
      externalId: "",
      address1: "",
      address2: "",
      city: "",
      state_or_province: "",
      postal_code: "",
      // NOTE: this is band aid
      country: "USA"
    },
    createPropertyFormSchema(hasPropertyOwner)
  );
  const [unitForms, setUnitForms] = useState([blankUnit]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [showMoreSettings, setShowMoreSettings] = useState<boolean>(false);
  const [debouncedQuery] = useDebouncedValue(
    {
      owner: propertyForm.values.owner,
      city: propertyForm.values.city || "",
      state_or_province: propertyForm.values.state_or_province || "",
      postal_code: propertyForm.values.postal_code || ""
    },
    500
  );

  const [usePropertyGroupTemplate, setUsePropertyGroupTemplate] =
    useState(false);

  const { data: propertyGroup, isLoading: isLoadingPropertyGroup } = useQuery({
    queryKey: ["property-groups-by-address", debouncedQuery],
    queryFn: () =>
      instance
        .get("/portal/v1/property_groups/find", {
          params: debouncedQuery
        })
        .then((res) => {
          setUsePropertyGroupTemplate(Boolean(res.data));
          return res.data;
        }),
    enabled: type.isBoomScreen
  });

  const isMA =
    partner?.property_management_system?.name ===
    PropertyManagementSystem.ManageAmerica;

  const { templates, isFetching } = useVerificationTemplatesController(999);

  const sortedTemplates = [...(templates || [])].sort(
    (a, b) => Number(b.default) - Number(a.default)
  );

  const renderedTemplates = showMoreSettings
    ? sortedTemplates
    : sortedTemplates.slice(0, 7);

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      const addressFields = googlePlaceToAddress(place);
      if (addressFields) {
        propertyForm.handleChange("address1")({
          target: { value: addressFields.address1 || "" }
        });
        propertyForm.handleChange("postal_code")({
          target: { value: addressFields.postalCode || "" }
        });
        propertyForm.handleChange("city")({
          target: { value: addressFields.city || "" }
        });
        propertyForm.handleChange("state_or_province")({
          target: { value: addressFields.stateOrProvince || "" }
        });
        propertyForm.handleChange("country")({
          target: { value: addressFields.country || "" }
        });
      }
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  const addUnitForm = () => {
    setUnitForms([...unitForms, blankUnit]);
  };

  const updateUnitForm = (index, field, value) => {
    setUnitForms((prev) => {
      const forms = [...prev];

      forms[index] = {
        ...forms[index],
        [field]: value
      };

      return forms;
    });
  };

  useEffect(() => {
    if (templates && templates.length)
      setSelectedTemplateId(templates.find((t) => t.default)?.id);
  }, [templates]);

  if (type.isSDK || !permissions.canManageProperties)
    return <Redirect href="/" />;

  return (
    <form
      onSubmit={propertyForm.onSubmit({
        onSuccess: async () => {
          try {
            const unitsToAdd = unitForms.reduce((acc, unit) => {
              if (unit.unitName && unit.rentAmount !== null)
                acc.push({
                  title: unit.unitName,
                  monthly_rent_amount: {
                    cents: unit.rentAmount * 100,
                    currency: "USD"
                  },
                  available_at: unit.available_at,
                  occupied: unit.occupied,
                  external_id: unit.external_id
                } as Unit);
              else if (
                isMA &&
                unit.unitName &&
                unit.homeRentAmount !== null &&
                unit.siteRentAmount !== null
              )
                acc.push({
                  title: unit.unitName,
                  monthly_rent_amount: {
                    cents: Math.round(
                      parseFloat(String(unit.homeRentAmount)) * 100 +
                        parseFloat(String(unit.siteRentAmount)) * 100
                    ),
                    currency: "USD"
                  },
                  home_rent_amount: {
                    cents: Math.round(
                      parseFloat(String(unit.homeRentAmount)) * 100
                    ),
                    currency: "USD"
                  },
                  site_rent_amount: {
                    cents: Math.round(
                      parseFloat(String(unit.siteRentAmount)) * 100
                    ),
                    currency: "USD"
                  },
                  available_at: unit.available_at,
                  occupied: unit.occupied,
                  external_id: unit.external_id
                } as Unit);

              return acc;
            }, [] as Unit[]);

            await addressApi
              .addProperty({
                use_property_group_verification_template:
                  usePropertyGroupTemplate,
                name: propertyForm.values.name,
                owner_name: propertyForm.values.owner || undefined,
                address: {
                  address1: propertyForm.values.address1,
                  address2: propertyForm.values.address2,
                  postal_code: propertyForm.values.postal_code,
                  city: propertyForm.values.city,
                  state_or_province: propertyForm.values.state_or_province,
                  country: propertyForm.values.country
                },
                units: unitsToAdd as Unit[],
                external_id: propertyForm.values.externalId,
                verification_template_id: selectedTemplateId
              })
              .then((newProperty) => {
                if (isDrawerContent) {
                  toast.success(
                    `${dictionary.Property} ${propertyForm.values.name} was successfully created`
                  );
                  onSuccess?.(newProperty);
                  onClose?.();
                } else navigate("/properties");
              });
          } catch (err) {
            toast.error(getErrorMessage(err));
          }
        },
        onError: () => {
          toast.error(
            `${dictionary.Property} name${
              hasPropertyOwner ? ", owner" : ""
            } and address cannot be empty`
          );
        }
      })}
    >
      <Layout
        sectionClassName={isDrawerContent ? "bg-white" : ""}
        displayAside={!isDrawerContent}
        header={
          isDrawerContent ? undefined : (
            <div className="flex gap-2.5 p-3">
              <BreadCrumbs
                crumbs={[
                  {
                    title: dictionary.Properties,
                    href: "/properties"
                  },
                  {
                    title: `Add ${dictionary.property}`,
                    href: "/properties/add"
                  }
                ]}
              />
            </div>
          )
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => (isDrawerContent ? onClose?.() : navigate(-1))}
              className="w-[125px]"
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              className="w-[125px]"
              type="submit"
              disabled={
                // disabled if any unit form is empty
                isSaveUnitDisabled(unitForms, isMA)
              }
            >
              Add
            </Button>
          </div>
        }
      >
        {isDrawerContent ? (
          <>
            <div className="flex items-center justify-between py-4">
              <h1 className="text-xl font-bold text-content-1000">
                Add {dictionary.property}
              </h1>
              <IconButton
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                borderLess
                className="!justify-end"
                onClick={onClose}
                Icon={XClose}
              ></IconButton>
            </div>
            <div className="mb-2 border-b border-alpha-80" />
          </>
        ) : (
          <>
            <h3 className="mb-3 text-2xl font-bold">
              Add {dictionary.property}
            </h3>
            <p className="mb-6 text-sm text-content-400">
              You can add new {dictionary.property}
            </p>
          </>
        )}

        <div className="w-full max-w-[760px]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                id="propertyName"
                value={propertyForm.values["name"]}
                error={propertyForm.errors.name}
                label={`${dictionary.Property} name`}
                required
                placeholder={`${dictionary.Property} name`}
                onChange={(e) => {
                  propertyForm.handleChange("name")(e);
                }}
              />
            </div>
            <div>
              <Input
                id="externalId"
                label={`${dictionary.Property} ID`}
                placeholder={`${dictionary.Property} ID`}
                onChange={(e) => {
                  propertyForm.handleChange("externalId")(e);
                }}
              />
            </div>
            <Show if={hasPropertyOwner}>
              <div>
                <Input
                  id="propertyOwner"
                  error={propertyForm.errors.owner}
                  label="Owner"
                  required
                  placeholder="Rentex"
                  onChange={(e) => {
                    propertyForm.handleChange("owner")(e);
                  }}
                />
              </div>
            </Show>
          </div>
          <div className="my-6 border-t border-alpha-80" />
          <div className="grid grid-cols-2 gap-4 ">
            <div className="w-full">
              <Input
                id="selectedPlace"
                label="Street Address"
                required
                placeholder="2972 Westheimer Rd"
                // @ts-expect-error
                ref={ref}
                value={propertyForm.values.address1}
                onChange={propertyForm.handleChange("address1")}
                error={propertyForm.errors.address1}
              />
            </div>
            <div className="w-full">
              <Input
                id="address2"
                name="address2"
                placeholder="Apartment 12"
                value={propertyForm.values.address2}
                onChange={propertyForm.handleChange("address2")}
                error={propertyForm.errors.address2}
                label="Apartment, suite, etc."
              />
            </div>
            <div className="w-full">
              <Input
                id="city"
                name="city"
                label="City"
                required
                placeholder="Austin"
                value={propertyForm.values.city}
                onChange={propertyForm.handleChange("city")}
                error={propertyForm.errors.city}
              />
            </div>
            <div className="flex w-full justify-end gap-4">
              <div className="mb-2 mt-auto h-fit flex-1">
                <Select
                  id="state_or_province"
                  label="State"
                  placeholder="State"
                  value={propertyForm.values.state_or_province}
                  searchable
                  options={states}
                  hideOptional
                  onChange={(e) =>
                    propertyForm.handleChange("state_or_province")(e)
                  }
                  error={propertyForm.errors.state_or_province}
                />
                <p className="text-danger-500 text-xs">
                  {propertyForm.errors.state_or_province}
                </p>
              </div>
              <div className="flex-1">
                <Input
                  id="postal_code"
                  name="postalCode"
                  required
                  label="Zip"
                  placeholder="11111"
                  value={propertyForm.values.postal_code}
                  onChange={propertyForm.handleChange("postal_code")}
                  error={propertyForm.errors.postal_code}
                />
              </div>
            </div>
          </div>

          <Show if={type.isBoomScreen}>
            <Show if={isLoadingPropertyGroup}>
              <div className="flex justify-center items-center h-[100px]">
                <Loader />
              </div>
            </Show>
            <Hide if={isLoadingPropertyGroup && !propertyGroup}>
              <div className="border-t border-alpha-80 pt-6 mt-2 space-y-4">
                <Card className="p-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">Property Group</p>
                    <ChevronRight className="size-4 stroke-content-100" />
                    <p className="text-sm font-medium text-content-500">
                      {propertyGroup?.name}
                    </p>
                  </div>
                  <p className="text-sm text-content-400">
                    {dictionary.Property} will be added to the{" "}
                    {propertyGroup?.name} {dictionary.property} group
                  </p>
                </Card>
              </div>
            </Hide>
          </Show>

          <div className="my-6 border-t border-alpha-80" />
          <Show if={type.isBoomScreen}>
            <>
              <p className="mb-4 text-base font-bold">Screening settings</p>

              <Show if={propertyGroup}>
                <div className="mb-4">
                  <Switch
                    label={`Use ${dictionary.property} group template`}
                    checked={usePropertyGroupTemplate}
                    onChange={() =>
                      setUsePropertyGroupTemplate(!usePropertyGroupTemplate)
                    }
                  />
                </div>
              </Show>
              <Show if={templates && !templates.length}>
                <p className="mb-2 text-sm text-content-500">
                  Hm.. Looks like you don&apos;t have any templates. Create one
                </p>
              </Show>
              <div className="mb-6 flex flex-wrap gap-4">
                {isFetching ? (
                  <div className="mx-10 flex min-h-[106px] items-center justify-center">
                    <Loader />
                  </div>
                ) : (
                  <>
                    {!usePropertyGroupTemplate ? (
                      <Link to="/verification-templates?create-mode=true">
                        <CheckboxCard
                          disabled={usePropertyGroupTemplate}
                          checked={false}
                          id="create-template"
                          wrapperClassName="border-primary-500 border-dashed bg-primary-20 hover:bg-primary-50 rounded-lg"
                          label={
                            <p className="w-full text-center text-sm font-medium text-primary-500">
                              Create template
                            </p>
                          }
                          name="create-template"
                          onChange={() => {}}
                          className="flex min-h-[76px] w-[176px] flex-col items-center  justify-center gap-2 rounded-lg border-2 border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50"
                        />
                      </Link>
                    ) : (
                      <CheckboxCard
                        disabled={true}
                        checked={false}
                        id="create-template"
                        wrapperClassName="border-primary-500 border-dashed bg-primary-20 opacity-50 rounded-lg"
                        label={
                          <p className="w-full text-center text-sm font-medium text-primary-500">
                            Create template
                          </p>
                        }
                        name="create-template"
                        onChange={() => {}}
                        className="flex min-h-[76px] w-[176px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary-500 bg-primary-20"
                      />
                    )}
                    {renderedTemplates.map((template) => (
                      <CheckboxCard
                        disabled={usePropertyGroupTemplate}
                        key={template.id}
                        checked={
                          selectedTemplateId === template.id &&
                          !usePropertyGroupTemplate
                        }
                        id={template.id}
                        wrapperClassName="border-alpha-100 animate-appear"
                        label={
                          <div className="relative w-full">
                            <Show if={template.default}>
                              <p className="absolute -right-4 -top-4 rounded-bl-lg rounded-tr-xl border-b border-l border-alpha-100 bg-white px-2 text-xs font-medium">
                                Default
                              </p>
                            </Show>
                            <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium text-content-600">
                              {template.name}
                            </p>
                            <Button
                              variant={ButtonVariant.TEXT}
                              disabled={usePropertyGroupTemplate}
                              onClick={() => {
                                setSelectedTemplateId(template.id);
                                setShowReviewModal(true);
                              }}
                            >
                              Review
                            </Button>
                          </div>
                        }
                        name="name"
                        onChange={() => setSelectedTemplateId(template.id)}
                        className="flex max-h-[76px] w-[176px] justify-start"
                      />
                    ))}
                  </>
                )}
              </div>
              <Hide if={(templates?.length || 0) < 7}>
                <div className="flex justify-center">
                  <Button
                    Icon={showMoreSettings ? ChevronUp : ChevronDown}
                    borderLess
                    variant={ButtonVariant.BASE}
                    onClick={() => {
                      setShowMoreSettings((prev) => !prev);
                    }}
                  >
                    {showMoreSettings ? "Show less" : "Show more"}
                  </Button>
                </div>
              </Hide>
              <div className="my-6 border-t border-alpha-80" />
            </>
          </Show>
          <p className="mb-2.5 text-base font-bold">Units</p>
          <div className="">
            <UnitForms
              unitForms={unitForms as any[]}
              updateUnitForm={updateUnitForm}
              removeUnitForm={(idx) =>
                setUnitForms((prev) => prev.filter((_, index) => idx !== index))
              }
            />
          </div>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={addUnitForm}
            Icon={Plus}
            type="button"
          >
            Add {dictionary.unit}
          </Button>
        </div>
      </Layout>
      <TemplateReviewModal
        opened={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        id={selectedTemplateId}
      />
    </form>
  );
};
